import { useEffect, useCallback } from 'react';
import { useCookieConsent } from '../contexts/CookieConsentContext';

const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || null;

/**
 * TrackingEnforcer — Consent enforcement layer
 * 
 * Controls all tracking scripts based on cookie consent:
 * - Analytics: PostHog, GA4
 * - Marketing: Future ad pixels (FB, LinkedIn, etc.)
 * - Preferences: localStorage theme/UI state
 * 
 * GDPR Art. 6(1)(a): No tracking fires until explicit consent.
 */
const TrackingEnforcer = () => {
  const { consent, hasConsented } = useCookieConsent();

  // === PostHog Control ===
  useEffect(() => {
    if (!window.posthog) return;
    if (!hasConsented) {
      // No consent yet — opt out by default (GDPR: no tracking before consent)
      window.posthog.opt_out_capturing();
      return;
    }
    if (consent.analytics) {
      window.posthog.opt_in_capturing();
    } else {
      window.posthog.opt_out_capturing();
    }
  }, [consent.analytics, hasConsented]);

  // === GA4 Control ===
  useEffect(() => {
    if (!GA_MEASUREMENT_ID) return;

    if (hasConsented && consent.analytics) {
      // Inject GA4 script if not already present
      if (!document.getElementById('ga4-script')) {
        const script = document.createElement('script');
        script.id = 'ga4-script';
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
        document.head.appendChild(script);

        window.dataLayer = window.dataLayer || [];
        function gtag() { window.dataLayer.push(arguments); }
        window.gtag = gtag;
        gtag('js', new Date());
        gtag('config', GA_MEASUREMENT_ID, {
          anonymize_ip: true,
          cookie_flags: 'SameSite=None;Secure',
        });
      }
    } else {
      // Remove GA4 script and clean up
      const script = document.getElementById('ga4-script');
      if (script) script.remove();

      // Revoke GA cookies
      if (window.gtag) {
        window.gtag('consent', 'update', {
          analytics_storage: 'denied',
        });
      }
      // Clear GA cookies
      document.cookie.split(';').forEach(c => {
        const name = c.trim().split('=')[0];
        if (name.startsWith('_ga') || name.startsWith('_gid')) {
          document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${window.location.hostname}`;
          document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
        }
      });
      window.dataLayer = undefined;
      window.gtag = undefined;
    }
  }, [consent.analytics, hasConsented]);

  // === Marketing Pixels Control ===
  useEffect(() => {
    if (!hasConsented || !consent.marketing) {
      // Remove any marketing pixels
      ['fb-pixel-script', 'li-insight-script'].forEach(id => {
        const el = document.getElementById(id);
        if (el) el.remove();
      });
      // Clear marketing cookies
      document.cookie.split(';').forEach(c => {
        const name = c.trim().split('=')[0];
        if (name.startsWith('_fbp') || name.startsWith('_fbc') || name.startsWith('li_')) {
          document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${window.location.hostname}`;
          document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
        }
      });
    }
    // Marketing scripts would be injected here when pixel IDs are configured
  }, [consent.marketing, hasConsented]);

  return null; // Invisible enforcement layer
};

/**
 * Hook: useConsentGuard
 * Check if a specific consent category is granted before performing an action.
 * 
 * Usage:
 *   const { isAllowed, requestConsent } = useConsentGuard('analytics');
 *   if (isAllowed) { track(...) }
 */
export const useConsentGuard = (category) => {
  const { consent, hasConsented, openPreferences } = useCookieConsent();
  
  const isAllowed = hasConsented && consent[category];
  
  const requestConsent = useCallback(() => {
    if (!isAllowed) openPreferences();
  }, [isAllowed, openPreferences]);

  return { isAllowed, requestConsent };
};

/**
 * Consent-aware event tracking.
 * Only fires if analytics consent is granted.
 */
export const trackEvent = (eventName, properties = {}) => {
  // PostHog
  if (window.posthog && !window.posthog.has_opted_out_capturing()) {
    window.posthog.capture(eventName, properties);
  }
  // GA4
  if (window.gtag) {
    window.gtag('event', eventName, properties);
  }
};

export default TrackingEnforcer;
